import { FC, useCallback, useContext, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Button,
  Card,
  Empty,
  Layout,
  List,
  Popconfirm,
  Space,
  App as AntdApp,
} from "antd";
import {
  View,
  useAppContext,
  useClientContext,
  useNavigation,
} from "@data-root/react";
import { Snapshot } from "@data-root/types";
import { DeleteOutlined, EditOutlined, SaveOutlined } from "@ant-design/icons";
import { ErrorView } from "@data-root/error-boundary";

const { Content } = Layout;
const { Meta } = Card;

interface SnapshotRowProps {
  snapshot: Snapshot;
}

export const SnapshotRow: FC<SnapshotRowProps> = ({ snapshot }) => {
  const antdApp = AntdApp.useApp();

  const { core } = useClientContext();
  const { appSpecifier, getTable } = useAppContext();
  const { navigation } = useNavigation();

  const table = useMemo(
    () => getTable(snapshot.tableId),
    [getTable, snapshot.tableId]
  );

  const onEdit = useCallback(() => {
    if (!snapshot.viewId) {
      antdApp.notification.error({
        message: "No se puede editar",
        description: "No se puede editar esta fila",
      });
      return;
    }

    navigation.navigate(snapshot.viewId!, { rowId: snapshot.rowId });
  }, [antdApp.notification, navigation, snapshot.rowId, snapshot.viewId]);

  const onSave = useCallback(async () => {
    try {
      await core.commitSnapshot(snapshot);
    } catch (error) {
      antdApp.notification.error({
        message: "Error al guardar",
        description: "Ocurrió un error al guardar los cambios",
      });
    }
  }, [antdApp.notification, core, snapshot]);

  const onDelete = useCallback(async () => {
    try {
      await core.deleteSnapshot(appSpecifier, table.id, snapshot.rowId);
    } catch (error) {
      antdApp.notification.error({
        message: "Error al eliminar",
        description: "Ocurrió un error al eliminar los datos",
      });
    }
  }, [antdApp.notification, appSpecifier, core, snapshot.rowId, table.id]);

  return (
    <Card
      hoverable
      style={{ width: "100%" }}
      type="inner"
      actions={[
        <Popconfirm
          title="Eliminar permanentemente"
          description="¿Estás seguro de querer eliminar permanentemente esta fila?"
          okText="Si"
          cancelText="No"
          icon={<DeleteOutlined />}
          onConfirm={onDelete}
        >
          <Button danger icon={<DeleteOutlined />}>
            Eliminar permanentemente
          </Button>
        </Popconfirm>,
        <Button icon={<SaveOutlined />} onClick={onSave}>
          Guardar
        </Button>,
      ]}
      extra={
        <Button
          type="primary"
          icon={<EditOutlined />}
          onClick={onEdit}
          disabled={!snapshot.viewId}
        >
          Continuar edición
        </Button>
      }
      title={
        <Space>
          <div style={{ userSelect: "none" }}>{table.name}</div>
        </Space>
      }
    >
      <Meta description={snapshot.created} />
    </Card>
  );
};

export const DraftsScreen = () => {
  const { core } = useClientContext();
  const { appSpecifier } = useAppContext();

  const { data: snapshots, error } = useQuery({
    queryKey: ["drafts", appSpecifier],
    queryFn: async () => {
      const snapshots = await core.getSnapshots(appSpecifier);
      return snapshots;
    },
    placeholderData: [],
  });

  if (error) return <ErrorView error={error} />;

  return (
    <Layout>
      <Content style={{ overflow: "auto" }}>
        {snapshots.length === 0 ? (
          <View style={{ height: "100%" }}>
            <Empty style={{ margin: "auto" }} />
          </View>
        ) : (
          <List
            split={false}
            dataSource={snapshots}
            rowKey={(row) => row.rowId}
            renderItem={(snapshot) => (
              <List.Item>
                <SnapshotRow snapshot={snapshot} />
              </List.Item>
            )}
          />
        )}
      </Content>
    </Layout>
  );
};
